import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TaskStatusCategory } from './taskStatusesApi';

// --- Types (mirror backend src/lib/dashboardService.ts) --------------------

export type ProjectStatus =
  | 'PLANNING'
  | 'ACTIVE'
  | 'ON_HOLD'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'ALMOST_DONE'
  | 'LEAD'
  | 'IN_HOUSE';
export type ProjectHealth = 'ON_TRACK' | 'AT_RISK' | 'OFF_TRACK';

export interface FinancialSummary {
  totalRevenue: number;
  totalExpenses: number;
  profit: number;
  profitMargin: number;
  tvaColectat: number;
  tvaDeductibil: number;
  tvaDePlata: number;
  currency: string;
}

export interface DashboardProjectCard {
  id: string;
  name: string;
  status: ProjectStatus;
  health: ProjectHealth;
  deadline: string | null;
  budget: number | null;
  /** Currency the budget is in (RON | EUR). */
  budgetCurrency: string;
  owner: { id: string; name: string } | null;
  memberCount: number;
  openTaskCount: number;
}

export interface TeamAllocationEntry {
  person: { id: string; name: string; title: string | null; capacity: number };
  projects: { projectId: string; projectName: string; percentage: number }[];
  totalPercent: number;
  utilizationPercent: number;
  overAllocated: boolean;
}

export interface DashboardSummary {
  financials: FinancialSummary;
  projects: DashboardProjectCard[];
  teamAllocation: TeamAllocationEntry[];
}

export interface DashboardStats {
  totalProjects: number;
  activeProjects: number;
  atRiskProjects: number;
  totalPeople: number;
  openInvoices: number;
  openTasks: number;
  overdueInvoices: number;
  invoicesTracked: boolean;
}

export interface RecentActivityItem {
  id: string;
  type: 'project' | 'invoice' | 'task';
  action: 'created' | 'updated';
  title: string;
  description: string;
  actor: string | null;
  timestamp: string;
  /** For `type === 'task'`: the task as the shared task row draws it. */
  task?: DashboardUrgentTask;
}

export interface SummaryFilters {
  year?: number;
  status?: ProjectStatus;
  health?: ProjectHealth;
  limit?: number;
  // Firm scope (multi-company): undefined = all firms.
  companyId?: string;
}

// --- COO Executive Command Center (CLE-256) --------------------------------
// Mirror backend src/lib/dashboardService.ts.

export type TaskPriority = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface DashboardKpis {
  revenueThisMonth: number;
  expensesThisMonth: number;
  profitThisMonth: number;
  profitMargin: number;
  revenueTrendPct: number;
  expensesTrendPct: number;
  unpaidReceivables: number;
  unpaidReceivablesCount: number;
  payablesDue: number;
  payablesDueCount: number;
  urgentTaskCount: number;
  overdueTaskCount: number;
  criticalTaskCount: number;
  currency: string;
}

export interface DashboardTodayInvoice {
  id: string;
  invoiceNumber: string;
  party: string | null;
  amount: number;
  dueDate: string | null;
  status: string;
  currency: string;
}

export interface DashboardUrgentTask {
  id: string;
  identifier: string | null;
  title: string;
  projectName: string | null;
  assignee: string | null;
  priority: TaskPriority;
  /** Stable status slug (configurable statuses). */
  status: string;
  statusLabel: string;
  statusColor: string;
  statusCategory: TaskStatusCategory;
  dueDate: string | null;
  daysOverdue: number;
}

export interface DashboardToday {
  incomingToday: DashboardTodayInvoice[];
  outgoingToday: DashboardTodayInvoice[];
  invoicesIssuedToday: DashboardTodayInvoice[];
  invoicesReceivedToday: DashboardTodayInvoice[];
  overdueTasks: DashboardUrgentTask[];
  criticalTasks: DashboardUrgentTask[];
  blockedTasks: DashboardUrgentTask[];
}

export interface UpcomingPayment {
  id: string;
  type: 'income' | 'outcome';
  invoiceNumber: string;
  party: string | null;
  amount: number;
  dueDate: string;
  status: string;
  currency: string;
}

export interface UpcomingPayments {
  days: number;
  items: UpcomingPayment[];
  totalIncome: number;
  totalOutcome: number;
  netTotal: number;
  currency: string;
}

// --- Overdue invoices per client (reminder widget) --------------------------
// Mirror backend src/lib/overdueInvoices.ts.

export interface OverdueInvoiceItem {
  id: string;
  series: string;
  number: string;
  issueDate: string | null;
  dueDate: string;
  daysOverdue: number;
  totalAmount: number;
  paidAmount: number;
  outstanding: number;
  currency: string;
  paymentStatus: string;
}

export interface OverdueClientGroup {
  /** Null when the SmartBill partner could not be matched to a CRM client. */
  clientId: string | null;
  clientName: string;
  cif: string | null;
  email: string | null;
  emailCandidates: string[];
  invoiceCount: number;
  totalOverdue: number;
  currency: string;
  maxDaysOverdue: number;
  lastReminderAt: string | null;
  invoices: OverdueInvoiceItem[];
}

export interface OverdueInvoicesResult {
  clients: OverdueClientGroup[];
  totalOverdue: number;
  clientCount: number;
}

export interface TeamAllocationGridEntry {
  person: { id: string; name: string; title: string | null };
  projects: { projectId: string; projectName: string }[];
  inProgressTasks: number;
  utilizationPercent: number;
  status: 'free' | 'busy' | 'overloaded';
  hasOverdueTasks: boolean;
}

export interface ActivityFeedItem extends RecentActivityItem {
  link: string | null;
}

// --- Dashboard charts + per-person tasks (CLE-257) -------------------------

export interface RevenueByProjectSlice {
  projectId: string | null;
  projectName: string;
  amount: number;
  percent: number;
}

export interface CashflowMonthPoint {
  period: string;
  label: string;
  income: number;
  expenses: number;
  profit: number;
}

export interface DashboardCharts {
  revenueByProject: RevenueByProjectSlice[];
  cashflow6m: CashflowMonthPoint[];
  revenueSparkline: number[];
  expensesSparkline: number[];
  profitSparkline: number[];
  currency: string;
}

export interface TeamMemberTasks {
  person: { id: string; name: string; title: string | null };
  tasks: DashboardUrgentTask[];
  inProgressCount: number;
  overdueCount: number;
  utilizationPercent: number;
}

// Standard success envelope: { success: true, data: <T> }.
interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function getDashboardSummary(filters: SummaryFilters = {}): Promise<DashboardSummary> {
  const query = buildQuery({
    year: filters.year,
    status: filters.status,
    health: filters.health,
    limit: filters.limit,
    companyId: filters.companyId,
  });
  const res = await apiFetch<Envelope<DashboardSummary>>(`/api/dashboard/summary${query}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getDashboardStats(companyId?: string): Promise<DashboardStats> {
  const res = await apiFetch<Envelope<DashboardStats>>(
    `/api/dashboard/stats${buildQuery({ companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getRecentActivity(
  limit = 10,
  companyId?: string,
): Promise<RecentActivityItem[]> {
  const res = await apiFetch<Envelope<RecentActivityItem[]>>(
    `/api/dashboard/recent-activity${buildQuery({ limit, companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

// --- COO Executive Command Center (CLE-256) --------------------------------

export async function getDashboardKpis(companyId?: string): Promise<DashboardKpis> {
  const res = await apiFetch<Envelope<DashboardKpis>>(
    `/api/dashboard/kpis${buildQuery({ companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getDashboardToday(companyId?: string): Promise<DashboardToday> {
  const res = await apiFetch<Envelope<DashboardToday>>(
    `/api/dashboard/today${buildQuery({ companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getUpcomingPayments(
  days = 14,
  companyId?: string,
): Promise<UpcomingPayments> {
  const res = await apiFetch<Envelope<UpcomingPayments>>(
    `/api/dashboard/upcoming-payments${buildQuery({ days, companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getOverdueInvoices(companyId?: string): Promise<OverdueInvoicesResult> {
  const res = await apiFetch<Envelope<OverdueInvoicesResult>>(
    `/api/dashboard/overdue-invoices${buildQuery({ companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getTeamAllocationGrid(): Promise<TeamAllocationGridEntry[]> {
  const res = await apiFetch<Envelope<TeamAllocationGridEntry[]>>(
    '/api/dashboard/team-allocation',
    {
      headers: authHeaders(),
    },
  );
  return res.data;
}

export async function getActivityFeed(limit = 15, companyId?: string): Promise<ActivityFeedItem[]> {
  const res = await apiFetch<Envelope<ActivityFeedItem[]>>(
    `/api/dashboard/activity-feed${buildQuery({ limit, companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

// --- Dashboard charts + per-person tasks (CLE-257) -------------------------

export async function getDashboardCharts(companyId?: string): Promise<DashboardCharts> {
  const res = await apiFetch<Envelope<DashboardCharts>>(
    `/api/dashboard/charts${buildQuery({ companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getTeamTasks(companyId?: string): Promise<TeamMemberTasks[]> {
  const res = await apiFetch<Envelope<TeamMemberTasks[]>>(
    `/api/dashboard/team-tasks${buildQuery({ companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}
